import {
  DELETION_FLAG,
  FALL_RISK,
  type FacilityKey,
  RESIDENT_STATE,
  Retry,
  type Status,
  TEXT_TO_SPEECH_MODE,
  type UserKey,
  t as tt,
} from "@packages/core";
import React, {
  Fragment,
  type FragmentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import { type Merge } from "type-fest";
import ALERT__PATH from "../../assets/alert.mp3";
import {
  nativeApplication,
  useBedInfiniteQuery,
  useFacilityQuery,
  useLatestStatusQuery,
  useNativeApplicationRevisionQuery,
  useResidentInfiniteQuery,
  useSelectedGroupKeys,
  useTextToSpeechRate,
  useTextToSpeechVolume,
  useVolume,
} from "../../services";

export type AlertPlayerProperties = Merge<
  React.ComponentProps<"audio">,
  {
    children?: never;
    /**
     * **施設キー**
     * 有効な入居者を監視する施設の識別子。
     */
    facilityKey: FacilityKey;
    preload?: never;
    ref?: never;
    src?: never;
  }
>;

export function AlertPlayer({ facilityKey, ...properties }: AlertPlayerProperties) {
  const audio = useRef<HTMLAudioElement>(null);
  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const facilityQuery = useFacilityQuery(facilityKey);
  const [statuses, setStatuses] = useState(new Array<Status>());
  const residentInfiniteQuery = useResidentInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const [selectedGroupKeys] = useSelectedGroupKeys(facilityKey);
  const nativeApplicationRevisionQuery = useNativeApplicationRevisionQuery();
  const [playing, setPlaying] = useState(false);
  const [volume] = useVolume();
  const [alertedAts, setAlertedAts] = useState(new Map<UserKey, Temporal.PlainDateTime>());
  const [textToSpeechVolume] = useTextToSpeechVolume();
  const [textToSpeechRate] = useTextToSpeechRate();
  const toastId = useRef<string | number | undefined>(undefined);

  const handleStatusChange = useCallback(function (status: Status) {
    setStatuses(function (statuses) {
      return statuses.concat([status]);
    });
  }, []);

  const beds = useMemo(
    function () {
      return bedInfiniteQuery.data?.pages.flatMap(function (page) {
        return page.beds;
      });
    },
    [bedInfiniteQuery.data],
  );

  useEffect(
    function () {
      if (playing || statuses.length === 0) {
        return;
      }

      const status = statuses.at(0);

      setPlaying(true);
      (async function () {
        if (!audio.current) {
          console.error("`<audio />`が参照できません。");
          toastId.current = toast.error(tt({ ja: "音声再生機能が存在しません。" }), {
            duration: Infinity,
            id: toastId.current,
          });
          return;
        }

        if (bedInfiniteQuery.isLoading) {
          console.debug("ベッドを取得中なので、アラート音声の再生を試みません。");
          return;
        }

        if (facilityQuery.isLoading) {
          console.debug("施設情報を取得中なので、アラート音声の再生を試みません。");
          return;
        }

        if (residentInfiniteQuery.isLoading) {
          console.debug("入居者情報を取得中なので、アラート音声の再生を試みません。");
          return;
        }

        if (!status || !status.userKey || !status.alerted || status.confirmed) {
          return;
        }

        const { userKey } = status;

        if (status.alertedAt) {
          const alertedAt = alertedAts.get(userKey);
          if (alertedAt && Temporal.PlainDateTime.compare(status.alertedAt, alertedAt) <= 0) {
            return;
          }
          setAlertedAts(function (alertedAts) {
            const newAlertedAts = new Map(alertedAts);
            newAlertedAts.set(userKey, status.alertedAt!);
            return newAlertedAts;
          });
        }

        const bed = bedInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.beds;
          })
          .find(function (bed) {
            return bed.userKey === status.userKey;
          });

        const facility = facilityQuery.data;

        const resident = residentInfiniteQuery.data?.pages
          .flatMap(function (page) {
            return page.residents;
          })
          .find(function (resident) {
            return resident.userKey === status.userKey;
          });

        if (bed && selectedGroupKeys.length > 0 && !selectedGroupKeys.includes(bed.groupKey!)) {
          return;
        }

        if (status.state === RESIDENT_STATE.GOT_UP) {
          const fallRisk = status.fallRisk ?? FALL_RISK.MILD;
          switch (fallRisk) {
            case FALL_RISK.MILD:
              audio.current.src = ALERT__PATH;
              break;
            case FALL_RISK.MODERATE:
              audio.current.src = ALERT__PATH;
              break;
            case FALL_RISK.SEVERE:
              audio.current.src = ALERT__PATH;
              break;
            default:
              audio.current.src = ALERT__PATH;
              break;
          }
        } else if (status.state === RESIDENT_STATE.WANDERING) {
          audio.current.src = ALERT__PATH;
        } else {
          audio.current.src = ALERT__PATH;
        }

        try {
          await Retry(
            Infinity,
            0,
            1000,
          )(
            async function () {
              if (
                (window.Android &&
                  nativeApplicationRevisionQuery.data !== undefined &&
                  nativeApplicationRevisionQuery.data >= 6) ||
                (window.webkit?.messageHandlers?.iOS &&
                  nativeApplicationRevisionQuery.data !== undefined &&
                  nativeApplicationRevisionQuery.data >= 6)
              ) {
                await nativeApplication.play("alert.mp3", { volume: 1.0 });
              } else {
                const { promise, reject, resolve } = Promise.withResolvers<void>();
                await audio.current?.play();
                audio.current?.addEventListener("ended", function () {
                  resolve();
                });
                audio.current?.addEventListener("error", function (event) {
                  reject(event.error);
                });
                await promise;
              }
            },
            function (error) {
              console.error("効果音が再生出来ません。", error);
              toastId.current = toast.error(
                tt({
                  ja:
                    "アラートサウンドの再生に失敗しました。" +
                    "再生するためにボタンやリンクなどで画面を少し操作してください。",
                }),
                { duration: Infinity, id: toastId.current },
              );
            },
          );

          if (toastId.current) {
            toast.dismiss(toastId.current);
            toastId.current = undefined;
          }
        } catch (error) {
          console.error("効果音が再生出来ません。", error);
          toastId.current = toast.error(tt({ ja: "アラートの再生に失敗しました。" }), {
            duration: Infinity,
            id: toastId.current,
          });
        }

        const textToSpeechText: Array<string> = [];
        if (status.state === RESIDENT_STATE.WANDERING) {
          textToSpeechText.push("徘徊中");
        } else {
          switch (status.fallRisk) {
            case FALL_RISK.MILD:
              textToSpeechText.push("配慮");
              break;
            case FALL_RISK.MODERATE:
              textToSpeechText.push("注意");
              break;
            case FALL_RISK.SEVERE:
              textToSpeechText.push("危険");
              break;
          }
        }

        switch (facility?.textToSpeechMode ?? TEXT_TO_SPEECH_MODE.RESIDENT_NAME) {
          case TEXT_TO_SPEECH_MODE.RESIDENT_NAME:
            if (resident) {
              textToSpeechText.push((resident.pronunciationName || resident.name) + "さん");
            }
            break;
          case TEXT_TO_SPEECH_MODE.ROOM_NAME_BED_NAME:
            if (bed) {
              const roomName = bed.roomPronunciation || bed.roomName;
              const bedName = bed.bedPronunciation || bed.bedName || "";
              textToSpeechText.push(roomName + (bedName ? "の" + bedName : ""));
            }
            break;
        }

        try {
          await Retry(
            Infinity,
            0,
            1000,
          )(
            async function () {
              if (
                window.Android ||
                (window.webkit?.messageHandlers?.iOS &&
                  nativeApplicationRevisionQuery.data !== undefined &&
                  nativeApplicationRevisionQuery.data >= 6)
              ) {
                await nativeApplication.speak(textToSpeechText.join("。"), {
                  pitch: 1.0,
                  rate: textToSpeechRate,
                  volume: textToSpeechVolume,
                });
              } else {
                const { promise, resolve, reject } = Promise.withResolvers<void>();
                const speechSynthesisUtterance = new SpeechSynthesisUtterance(
                  textToSpeechText.join("。"),
                );
                speechSynthesisUtterance.onend = function () {
                  resolve();
                };
                speechSynthesisUtterance.onerror = function (event) {
                  reject(event.error);
                };
                speechSynthesisUtterance.lang = "ja-JP";
                speechSynthesisUtterance.pitch = 1.0;
                speechSynthesisUtterance.rate = textToSpeechRate;
                speechSynthesisUtterance.volume = textToSpeechVolume;
                window.speechSynthesis.speak(speechSynthesisUtterance);
                await promise;
              }
            },
            function (error) {
              console.error("合成音声が再生出来ません。", error);
              toastId.current = toast.error(
                tt({
                  ja:
                    "アラートボイスの再生に失敗しました。" +
                    "再生するためにボタンやリンクなどで画面を少し操作してください。",
                }),
                { duration: Infinity, id: toastId.current },
              );
            },
          );

          if (toastId.current) {
            toast.dismiss(toastId.current);
            toastId.current = undefined;
          }
        } catch (error) {
          console.error("合成音声が再生出来ません。", error);
          toastId.current = toast.error(
            tt({
              ja:
                "アラートボイスの再生に失敗しました。" +
                "再生するためにボタンやリンクなどで画面を少し操作してください。",
            }),
            { duration: Infinity, id: toastId.current },
          );
        }

        if (toastId.current) {
          toast.dismiss(toastId.current);
          toastId.current = undefined;
        }
      })().finally(function () {
        setStatuses(function (statuses) {
          return statuses.filter(function (s) {
            return s !== status;
          });
        });
        setPlaying(false);
      });
    },
    [
      alertedAts,
      bedInfiniteQuery.data,
      bedInfiniteQuery.isLoading,
      facilityQuery.data,
      facilityQuery.isLoading,
      nativeApplicationRevisionQuery.data,
      playing,
      residentInfiniteQuery.data,
      residentInfiniteQuery.isLoading,
      selectedGroupKeys,
      statuses,
      textToSpeechRate,
      textToSpeechVolume,
      toastId,
    ],
  );

  useEffect(
    function () {
      if (audio.current) {
        audio.current.volume = volume;
      }
    },
    [volume],
  );

  return (
    <Fragment>
      {beds?.map(function (bed) {
        return (
          <ResidentWatcher
            key={bed.bedKey}
            onStatusChange={handleStatusChange}
            userKey={bed.userKey}
          />
        );
      })}

      <audio {...properties} preload="auto" ref={audio} />
    </Fragment>
  );
}

type ResidentWatcherProperties = Merge<
  FragmentProps,
  { userKey?: UserKey | null; onStatusChange?: { (status: Status): void } }
>;
function ResidentWatcher({ userKey, onStatusChange, ...properties }: ResidentWatcherProperties) {
  const latestStatusQuery = useLatestStatusQuery(userKey);

  useEffect(
    function () {
      if (!latestStatusQuery.isLoading && latestStatusQuery.data) {
        onStatusChange?.(latestStatusQuery.data);
      }
    },
    [latestStatusQuery.data, latestStatusQuery.isLoading, onStatusChange],
  );

  return <Fragment {...properties} />;
}
